/**
 * MessageActionsMenu — per-bubble dropdown menu, WhatsApp-Web style.
 *
 * Owner-spec 2026-05-31 (Phase 3 of 4). Replaces the temporary ↩ hover
 * icon from Phase 2. Hover a bubble → a ▼ chevron appears in the top
 * corner. Click → dropdown opens with the action list.
 *
 * Wired actions (per owner-spec: "אחרת תתחיל לצבור debt של כפתורים
 * שלא באמת עובדים"):
 *   - תגובה (Reply)         → onReply()
 *   - העתקה (Copy)          → clipboard
 *   - פרטי הודעה (Info)     → onInfo()
 *
 * Deliberately NOT rendered (no stubs):
 *   - הוספת אמוג'י, העברה, הצמדה, סימון בכוכב, מחיקה
 * Those land in later phases (4 for reactions; the rest open as needed).
 *
 * Props:
 *   msg      — the message object
 *   align    — 'right' | 'left' (which side of the bubble the trigger
 *              docks to; mirror via msg.fromMe in the caller).
 *   onReply  — () => void
 *   onCopy   — optional (msg) => void; default uses navigator.clipboard
 *              with msg.body || msg.caption || msg.mediaUrl.
 *   onInfo   — () => void
 */

import React, { useEffect, useRef, useState } from 'react';

const ITEM_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '8px 14px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  width: '100%',
  textAlign: 'right',
  fontSize: '13px',
  color: '#1a1a1a',
};

const Trigger = ({ align, onClick }) => (
  <button
    className="wa-actions-trigger"
    onClick={(e) => { e.stopPropagation(); onClick(e); }}
    title="פעולות"
    style={{
      position: 'absolute',
      top: '2px',
      [align]: '2px',
      background: 'rgba(0,0,0,0.45)',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '20px',
      height: '20px',
      fontSize: '11px',
      cursor: 'pointer',
      opacity: 0,
      transition: 'opacity 0.15s',
      lineHeight: 1,
    }}
  >
    ▾
  </button>
);

const Item = ({ icon, label, onClick }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
    style={ITEM_STYLE}
    onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f5f5'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
  >
    <span style={{ width: '20px', textAlign: 'center', fontSize: '14px' }}>{icon}</span>
    <span style={{ flex: 1 }}>{label}</span>
  </button>
);

const MessageActionsMenu = ({ msg, align = 'right', onReply, onCopy, onInfo }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close on outside click and on Esc.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleCopy = async () => {
    setOpen(false);
    try {
      if (onCopy) {
        onCopy(msg);
      } else {
        const text = msg.body || msg.caption || msg.mediaUrl || '';
        if (text && navigator.clipboard) {
          await navigator.clipboard.writeText(text);
        }
      }
    } catch {
      // Silent — copying fails on insecure contexts / restricted browsers.
      // Owner can fall back to manual select.
    }
  };

  const handleReply = () => {
    setOpen(false);
    if (onReply) onReply();
  };

  const handleInfo = () => {
    setOpen(false);
    if (onInfo) onInfo();
  };

  // Dock the dropdown panel on the same side as the trigger.
  const panelStyle = {
    position: 'absolute',
    top: '24px',
    [align]: '0',
    minWidth: '180px',
    background: 'white',
    boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
    borderRadius: '6px',
    paddingTop: '4px',
    paddingBottom: '4px',
    zIndex: 50,
  };

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'absolute',
        top: '0',
        [align]: '-26px',
        width: '24px',
        height: '24px',
      }}
    >
      <Trigger align={align} onClick={() => setOpen(o => !o)} />
      {open && (
        <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
          <Item icon="ℹ" label="פרטי הודעה" onClick={handleInfo} />
          <Item icon="↩" label="תגובה" onClick={handleReply} />
          <Item icon="⎘" label="העתקה" onClick={handleCopy} />
        </div>
      )}
    </div>
  );
};

export default MessageActionsMenu;
